// Quotation List page (route APP_ROUTES.QUOTATION.LIST = "/quotation-list").
// Shows one row per customer (WhatsApp contact) with the number of quotations sent to them.
// Clicking the count badge or the PDF action opens ViewQuotationModal to browse and re-send that customer's PDFs.
import Tabs from "./tabs";
import { useMemo } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useState } from "react";
import ViewQuotationModal from "./viewQuotationModal";
import { formatDate } from "../../../components/formatDateAndTime/formatDateAndTime";
import DataTableWithCheckbox from "../../../components/dataTableWithSearch/DataTableWithCheckBox";
import { NameCell } from "../../leadMangement/components/leadUi";
import { QR } from "../../qrCode/components/qrUi";
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';

const CrmQuotationList = () => {
    // quotationLogs are loaded by Tabs on mount.
    const { quotationLogs = [], status } = useSelector((state) => state.crmQuotation);

    // openQuotationModal toggles ViewQuotationModal; selectedRowData is the grouped customer row passed to it.
    const [state, setState] = useState({ openQuotationModal: false, selectedRowData: [] });

    // Table columns. "sno" is not in the grouped rows; DataTableWithCheckbox adds it (index + 1).
    const columns = useMemo(() => [
        { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
        {
            accessorKey: "name", header: "Name",
            Cell: ({ row }) => <NameCell name={row.original.name} />
        },
        // Count badge: clicking it opens the quotations modal for this customer.
        {
            accessorKey: "count", header: "Count", Cell: ({ row }) => (
                <span style={{ display: "inline-block" }}>
                    <Box
                        onClick={() => { setState(prev => ({ ...prev, openQuotationModal: true, selectedRowData: row?.original })); }}
                        component="span"
                        title="View quotations"
                        sx={{
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.6,
                            px: 1,
                            py: 0.3,
                            borderRadius: 5,
                            fontSize: 12,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            color: QR.blue,
                            bgcolor: `${QR.blue}12`,
                            border: `1px solid ${QR.blue}26`,
                            transition: "all .15s ease",
                            "&:hover": { bgcolor: QR.blue, color: "#fff" },
                        }}
                    >
                        <PictureAsPdfOutlinedIcon sx={{ fontSize: 14 }} />
                        {row.original.count}
                    </Box>
                </span>
            )
        },
        {
            accessorKey: "created_at",
            header: "Last Send Date",
            Cell: ({ row }) => (
                <Typography variant="body2" color="text.secondary">{formatDate(+row.original.created_at)}</Typography>
            )
        }
    ], []);

    // Group the flat log list by wa_contact_id into one row per customer:
    // { id, name, wa_contact_id, count, created_at, quotations: [{ quotation_no, quotation_path }] }.
    // Each customer's quotations are sorted newest first by the running number in "SB/Quote/<n>/<mm>/<yy-yy>".
    // Note: created_at comes from the first log seen for the contact, so "Last Send Date" appears to depend on API order.
    const result = [];

    quotationLogs.map(item => {
        const existing = result.find(r => r.wa_contact_id === item.wa_contact_id);

        if (existing) {
            existing.quotations.push({
                quotation_no: item.quotation_no,
                quotation_path: item.quotation_path || ""
            });

            existing.quotations.sort((a, b) => {
                const numA = Number(a.quotation_no.split("/")[2]);
                const numB = Number(b.quotation_no.split("/")[2]);
                return numB - numA;
            });

            existing.count = existing.quotations.length;
        } else {
            result.push({
                id: item.id,
                name: item.name,
                wa_contact_id: item.wa_contact_id,
                count: 1,
                created_at: +item.created_at,
                quotations: [
                    {
                        quotation_no: item.quotation_no,
                        quotation_path: item.quotation_path || ""
                    }
                ]
            });
        }
    });

    // Summary cards (display only, from the loaded logs)
    const stats = [
        { label: "Quotations Sent", value: quotationLogs.length, icon: <RequestQuoteIcon />, color: QR.main },
        { label: "Customers", value: result.length, icon: <PeopleAltOutlinedIcon />, color: QR.blue },
    ];

    return (
        <div>
            {/* Header card with summary stats; the table is rendered inside it. */}
            <Tabs stats={stats}>
                <DataTableWithCheckbox
                    columns={columns}
                    data={result}
                    status={status}
                    enableRowSelection={false}
                    searchFirst
                    emptyMessage="No quotations sent yet. Create one to get started."
                    // Row action: same as clicking the count badge.
                    renderRowActions={({ row }) => (
                        <Tooltip title="View quotations" arrow>
                            <IconButton
                                size="small"
                                onClick={() => { setState(prev => ({ ...prev, openQuotationModal: true, selectedRowData: row?.original })); }}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 2,
                                    color: QR.blue,
                                    bgcolor: `${QR.blue}10`,
                                    border: `1px solid ${QR.blue}26`,
                                    "&:hover": { bgcolor: QR.blue, color: "#fff" },
                                }}
                            >
                                <PictureAsPdfOutlinedIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                />
            </Tabs>
            {/* Modal listing this customer's sent PDFs with a WhatsApp "Re Send" button. */}
            <ViewQuotationModal show={state.openQuotationModal} handleClose={() => setState(prev => ({ ...prev, openQuotationModal: false }))} rowData={state.selectedRowData} />
        </div>
    );
};
export default CrmQuotationList;
